import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase, isOnline, withOfflineFallback, checkTableExists, handleSupabaseError } from '../utils/supabase'
import { useAuthStore } from './auth'

// Define the user profile interface
export interface UserProfile {
  id?: string
  auth_id?: string // Link to auth.user.id
  email?: string
  display_name?: string
  first_name?: string
  last_name?: string
  job_title?: string
  phone?: string
  avatar_url?: string | null
  created_at?: string
  updated_at?: string
  [key: string]: any // Allow for additional properties
}

export const useUserStore = defineStore('user', () => {
  // State
  const state = ref({
    profile: null as UserProfile | null,
    loading: false,
    error: null as string | null,
    initialized: false,
    profilesTable: null as string | null,
    userIdColumn: null as string | null
  })
  
  // Initialize from localStorage
  const initFromLocalStorage = () => {
    const authStore = useAuthStore();
    try {
      const storedProfile = localStorage.getItem('userProfile')
      if (storedProfile) {
        state.value.profile = JSON.parse(storedProfile)
        state.value.initialized = true
        console.log('User profile initialized from localStorage')
      }
    } catch (err) {
      console.error('Error initializing profile from localStorage:', err)
    }
  }
  
  // Call initialization

  // (If other functions/actions below use authStore, move the call inside those as well)
  initFromLocalStorage()
  
  // Detect the correct table and column names
  const detectDatabaseStructure = async () => {
    console.log('Detecting database structure...')
    
    // Check for user_profiles table
    const hasUserProfilesTable = await checkTableExists('user_profiles')
    if (hasUserProfilesTable) {
      state.value.profilesTable = 'user_profiles'
      console.log('Found user_profiles table')
      
      // Try to determine the user ID column
      try {
        // Try auth_id first
        const { data: authIdData, error: authIdError } = await supabase
          .from('user_profiles')
          .select('auth_id')
          .limit(1)
        
        if (!authIdError) {
          state.value.userIdColumn = 'auth_id'
          console.log('Using auth_id as the user ID column')
          return
        }
        
        // Try user_id next
        const { data: userIdData, error: userIdError } = await supabase
          .from('user_profiles')
          .select('user_id')
          .limit(1)
        
        if (!userIdError) {
          state.value.userIdColumn = 'user_id'
          console.log('Using user_id as the user ID column')
          return
        }
        
        // Try id last
        const { data: idData, error: idError } = await supabase
          .from('user_profiles')
          .select('id')
          .limit(1)
        
        if (!idError) {
          state.value.userIdColumn = 'id'
          console.log('Using id as the user ID column')
          return
        }
        
        console.warn('Could not determine user ID column, will use id as default')
        state.value.userIdColumn = 'id'
      } catch (err) {
        console.error('Error detecting user ID column:', err)
        state.value.userIdColumn = 'id' // Default fallback
      }
      return
    }
    
    // Check for profiles table
    const hasProfilesTable = await checkTableExists('profiles')
    if (hasProfilesTable) {
      state.value.profilesTable = 'profiles'
      console.log('Found profiles table')
      
      // Try to determine the user ID column
      try {
        // Try id first (most likely for profiles table)
        const { data: idData, error: idError } = await supabase
          .from('profiles')
          .select('id')
          .limit(1)
        
        if (!idError) {
          state.value.userIdColumn = 'id'
          console.log('Using id as the user ID column')
          return
        }
        
        // Try user_id next
        const { data: userIdData, error: userIdError } = await supabase
          .from('profiles')
          .select('user_id')
          .limit(1)
        
        if (!userIdError) {
          state.value.userIdColumn = 'user_id'
          console.log('Using user_id as the user ID column')
          return
        }
        
        // Try auth_id last
        const { data: authIdData, error: authIdError } = await supabase
          .from('profiles')
          .select('auth_id')
          .limit(1)
        
        if (!authIdError) {
          state.value.userIdColumn = 'auth_id'
          console.log('Using auth_id as the user ID column')
          return
        }
        
        console.warn('Could not determine user ID column, will use id as default')
        state.value.userIdColumn = 'id'
      } catch (err) {
        console.error('Error detecting user ID column:', err)
        state.value.userIdColumn = 'id' // Default fallback
      }
      return
    }
    
    console.warn('No profile tables found in the database')
    state.value.profilesTable = null
    state.value.userIdColumn = null
  }
  
  // Create a new profile table if none exists
  const createProfileTable = async () => {
    if (state.value.profilesTable) {
      console.log('Profile table already exists, no need to create')
      return { success: true, tableName: state.value.profilesTable }
    }
    
    console.log('Attempting to create profiles table...')
    
    try {
      // Create the profiles table with the standard Supabase structure
      const { error } = await supabase.rpc('create_profiles_table')
      
      if (error) {
        console.error('Error creating profiles table via RPC:', error)
        
        // Try direct SQL as fallback
        const { error: sqlError } = await supabase.from('profiles').insert({})
        
        if (sqlError && sqlError.code !== '42P01') {
          throw sqlError
        }
        
        // Check if table was created
        const hasProfilesTable = await checkTableExists('profiles')
        if (hasProfilesTable) {
          state.value.profilesTable = 'profiles'
          state.value.userIdColumn = 'id'
          console.log('Successfully created profiles table')
          return { success: true, tableName: 'profiles' }
        }
        
        throw new Error('Failed to create profiles table')
      }
      
      state.value.profilesTable = 'profiles'
      state.value.userIdColumn = 'id'
      console.log('Successfully created profiles table')
      return { success: true, tableName: 'profiles' }
    } catch (err) {
      console.error('Error creating profiles table:', err)
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
    }
  }
  
  // Initialize the user profile
  const initProfile = async () => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Initializing user profile...')
      
      // First check if we already have a profile in localStorage
      const storedProfile = localStorage.getItem('userProfile')
      if (storedProfile) {
        state.value.profile = JSON.parse(storedProfile)
        state.value.initialized = true
        console.log('User profile initialized from localStorage')
      }
      
      // If we're online, try to detect the database structure
      if (isOnline()) {
        await detectDatabaseStructure()
      }
      
      // If we have a user ID, try to fetch the profile from the database
      const authStore = useAuthStore();
      const userId = authStore.user.value?.id || localStorage.getItem('userId')
      
      if (userId && isOnline() && state.value.profilesTable && state.value.userIdColumn) {
        console.log(`Fetching profile for user ${userId} from ${state.value.profilesTable} table using ${state.value.userIdColumn} column`)
        
        const { data, error: fetchError } = await supabase
          .from(state.value.profilesTable)
          .select('*')
          .eq(state.value.userIdColumn, userId)
          .single()
        
        if (fetchError) {
          console.warn('Error fetching profile from database:', fetchError)
          
          // If the profile doesn't exist, create one
          if (fetchError.code === 'PGRST116') {
            console.log('Profile not found, creating a new one')
            return await createProfile()
          }
        } else if (data) {
          // Profile found in database
          state.value.profile = data
          localStorage.setItem('userProfile', JSON.stringify(data))
          state.value.initialized = true
          console.log('Profile fetched from database:', data)
          return { success: true, profile: data }
        }
      }
      
      // If we still don't have a profile but have a user, create one from user data
      if (!state.value.profile && authStore.user.value) {
        console.log('Creating profile from user data')
        return await createProfile()
      }
      
      // If we have a profile, consider initialization successful
      if (state.value.profile) {
        return { success: true, profile: state.value.profile, offline: !isOnline() }
      }
      
      // If we get here, we couldn't initialize the profile
      return { success: false, error: 'Could not initialize user profile' }
    } catch (err) {
      console.error('Error initializing profile:', err)
      state.value.error = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }
  
  // Create a new profile
  const createProfile = async () => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Creating new user profile')
      
      const authStore = useAuthStore();
      const user = authStore.user.value
      const userId = user?.id || localStorage.getItem('userId')
      
      if (!userId) {
        throw new Error('No user ID available to create profile')
      }
      
      // Create a profile object from available data
      const newProfile: UserProfile = {
        id: userId,
        auth_id: userId,
        email: user?.email || localStorage.getItem('userEmail') || '',
        display_name: user?.user_metadata?.display_name || 
                     localStorage.getItem('userDisplayName') || 
                     user?.email?.split('@')[0] || 
                     'User',
        first_name: user?.user_metadata?.first_name || 
                   localStorage.getItem('userFirstName') || 
                   '',
        last_name: user?.user_metadata?.last_name || 
                  localStorage.getItem('userLastName') || 
                  '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      // Store in localStorage first for offline resilience
      localStorage.setItem('userProfile', JSON.stringify(newProfile))
      state.value.profile = newProfile
      state.value.initialized = true
      
      // If we're online and have a profiles table, save to database
      if (isOnline() && state.value.profilesTable) {
        console.log(`Saving profile to ${state.value.profilesTable} table`)
        
        // Prepare the insert data with the correct ID field
        const insertData: any = { ...newProfile }
        
        // Make sure we have the right ID field
        if (state.value.userIdColumn && state.value.userIdColumn !== 'id') {
          insertData[state.value.userIdColumn] = userId
        }
        
        const { data, error } = await supabase
          .from(state.value.profilesTable)
          .upsert(insertData)
          .select()
          .single()
        
        if (error) {
          console.warn('Error saving profile to database:', error)
          // Continue with local profile
        } else if (data) {
          // Update with the returned data
          state.value.profile = data
          localStorage.setItem('userProfile', JSON.stringify(data))
          console.log('Profile saved to database:', data)
        }
      } else {
        console.log('Offline mode or no profiles table: profile saved to localStorage only')
      }
      
      return { success: true, profile: state.value.profile, offline: !isOnline() || !state.value.profilesTable }
    } catch (err) {
      console.error('Error creating profile:', err)
      state.value.error = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }
  
  // Update the user profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    state.value.loading = true
    state.value.error = null
    
    try {
      console.log('Updating user profile:', updates)
      
      if (!state.value.profile) {
        throw new Error('No profile to update')
      }
      
      // Update the local profile
      const updatedProfile = {
        ...state.value.profile,
        ...updates,
        updated_at: new Date().toISOString()
      }
      
      // Store in localStorage first for offline resilience
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile))
      state.value.profile = updatedProfile
      
      // If we're online and have a profiles table, save to database
      if (isOnline() && state.value.profilesTable && state.value.userIdColumn) {
        console.log(`Saving updated profile to ${state.value.profilesTable} table`)
        
        const { data, error } = await supabase
          .from(state.value.profilesTable)
          .update(updatedProfile)
          .eq(state.value.userIdColumn, updatedProfile.id || updatedProfile.auth_id)
          .select()
          .single()
        
        if (error) {
          console.warn('Error updating profile in database:', error)
          // Continue with local profile
        } else if (data) {
          // Update with the returned data
          state.value.profile = data
          localStorage.setItem('userProfile', JSON.stringify(data))
          console.log('Profile updated in database:', data)
        }
      } else {
        console.log('Offline mode or no profiles table: profile updated in localStorage only')
      }
      
      return { success: true, profile: state.value.profile, offline: !isOnline() || !state.value.profilesTable }
    } catch (err) {
      console.error('Error updating profile:', err)
      state.value.error = err instanceof Error ? err.message : 'Unknown error'
      return { success: false, error: state.value.error }
    } finally {
      state.value.loading = false
    }
  }
  
  // Fetch the user profile from the database
  const fetchProfile = async (userId?: string) => {
    state.value.loading = true
    state.value.error = null
    
    return await withOfflineFallback(
      // Online operation
      async () => {
        try {
          // Use provided userId or get from auth store or localStorage
          const authStore = useAuthStore();
      const id = userId || authStore.user.value?.id || localStorage.getItem('userId')
          
          if (!id) {
            throw new Error('No user ID available to fetch profile')
          }
          
          if (!state.value.profilesTable || !state.value.userIdColumn) {
            await detectDatabaseStructure()
            
            if (!state.value.profilesTable || !state.value.userIdColumn) {
              throw new Error('Could not determine profile table structure')
            }
          }
          
          console.log(`Fetching profile for user ${id} from ${state.value.profilesTable} table`)
          
          const { data, error } = await supabase
            .from(state.value.profilesTable)
            .select('*')
            .eq(state.value.userIdColumn, id)
            .single()
          
          if (error) {
            throw error
          }
          
          if (data) {
            state.value.profile = data
            localStorage.setItem('userProfile', JSON.stringify(data))
            state.value.initialized = true
            console.log('Profile fetched from database:', data)
            return { success: true, profile: data }
          }
          
          throw new Error('No profile found')
        } catch (err) {
          console.error('Error fetching profile:', err)
          state.value.error = err instanceof Error ? err.message : 'Unknown error'
          throw err
        } finally {
          state.value.loading = false
        }
      },
      // Offline fallback
      () => {
        console.log('Using locally stored profile')
        const storedProfile = localStorage.getItem('userProfile')
        
        if (storedProfile) {
          try {
            const profile = JSON.parse(storedProfile)
            state.value.profile = profile
            state.value.initialized = true
            state.value.loading = false
            return { success: true, profile, offline: true, error: undefined }
          } catch (err) {
            console.error('Error parsing stored profile:', err)
            state.value.error = 'Invalid stored profile'
            state.value.loading = false
            return { success: false, error: 'Invalid stored profile', offline: true, profile: undefined }
          }
        }
        
        state.value.loading = false
        return { success: false, error: 'No stored profile found', offline: true, profile: undefined }
      }
    )
  }
  
  return {
    // State
    state,
    profile: computed(() => state.value.profile),
    loading: computed(() => state.value.loading),
    error: computed(() => state.value.error),
    initialized: computed(() => state.value.initialized),
    
    // Actions
    initProfile,
    createProfile,
    updateProfile,
    fetchProfile
  }
})